import { CartAddEvent } from '@theme/events';

/*
  Layers locked Add to Cart (see snippets/layers-add.liquid).

  States: locked (no size) → open (size row showing) → ready (size chosen)
  → adding → added (tick for a moment) → locked again.
*/
class LayersAdd extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-layers-add-button]');
    this.sizes = this.querySelector('[data-layers-sizes]');
    this.sizeButtons = [...this.querySelectorAll('[data-layers-size]')];
    this.status = this.querySelector('[data-layers-status]');
    try {
      this.variants = JSON.parse(this.querySelector('[data-layers-variants]').textContent);
    } catch (e) {
      this.variants = [];
    }
    this.colourIndex = Number(this.dataset.colourIndex);
    this.sizeIndex = Number(this.dataset.sizeIndex);
    this.size = null;
    this.busy = false;

    this.button.addEventListener('click', this.onButton);
    this.sizeButtons.forEach((b) => b.addEventListener('click', this.onSize));

    // the card's colour swatches (theme component) tell us which colour is showing
    this.card = this.closest('product-card');
    this.card?.addEventListener('change', this.onCardChange);
    // close the size row when the pointer leaves the card, keep the chosen size
    this.card?.addEventListener('pointerleave', () => this.open(false));
    document.addEventListener('click', this.onOutside, true);

    this.refreshSizes();
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutside, true);
  }

  get colour() {
    if (this.dataset.colour) return this.dataset.colour;
    const checked = this.card?.querySelector('swatches-variant-picker-component input[type="radio"]:checked');
    if (checked) return checked.value;
    if (this.colourIndex < 0) return '';
    const first = this.variants.find((v) => v.available) || this.variants[0];
    return first ? first.colour : '';
  }

  variantFor(size) {
    const colour = this.colour;
    return this.variants.find(
      (v) => (this.colourIndex < 0 || v.colour === colour) && (this.sizeIndex < 0 || v.size === size)
    );
  }

  refreshSizes() {
    this.sizeButtons.forEach((b) => {
      const v = this.variantFor(b.dataset.layersSize);
      const soldOut = !v || !v.available;
      b.classList.toggle('is-sold-out', soldOut);
      b.disabled = soldOut;
      if (soldOut && this.size === b.dataset.layersSize) this.size = null;
    });
  }

  onCardChange = (e) => {
    if (e.target?.matches?.('swatches-variant-picker-component input[type="radio"]')) {
      this.refreshSizes();
      this.render();
    }
  };

  onOutside = (e) => {
    if (!this.contains(e.target) && this.sizes && !this.sizes.hidden) this.open(false);
  };

  onSize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.size = e.currentTarget.dataset.layersSize;
    this.render();
    // one tap on a size is the whole decision: add straight away
    this.add();
  };

  onButton = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.busy) return;
    // no size option at all, or size already chosen: add
    if (this.sizeIndex < 0 || this.size) {
      this.add();
      return;
    }
    this.open(this.sizes.hidden);
  };

  open(state) {
    if (!this.sizes) return;
    this.sizes.hidden = !state;
    this.button.setAttribute('aria-expanded', String(state));
    this.classList.toggle('is-open', state);
  }

  render() {
    const ready = this.sizeIndex < 0 || Boolean(this.size);
    this.classList.toggle('is-ready', ready);
    this.classList.toggle('is-locked', !ready);
    this.button.title = ready ? 'Add to cart' : 'Choose a size';
    this.sizeButtons.forEach((b) => b.setAttribute('aria-checked', String(b.dataset.layersSize === this.size)));
  }

  async add() {
    const variant = this.variantFor(this.size);
    if (!variant || !variant.available) {
      this.say('Choose a size');
      this.open(true);
      return;
    }
    this.busy = true;
    this.classList.add('is-adding');
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
    try {
      const res = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      document.dispatchEvent(
        new CartAddEvent({}, this.dataset.productId, {
          source: 'layers-add',
          productId: this.dataset.productId,
          variantId: String(variant.id),
          itemCount: 1,
          sections: data.sections,
        })
      );
      this.say(`${this.dataset.productTitle}, ${this.size || ''} added to your cart`);
      this.classList.remove('is-adding');
      this.classList.add('is-added');
      this.open(false);
      setTimeout(() => {
        this.classList.remove('is-added');
        this.size = null;
        this.render();
      }, 1400);
    } catch (err) {
      this.classList.remove('is-adding');
      this.say(err.message);
    } finally {
      this.busy = false;
    }
  }

  say(text) {
    if (this.status) this.status.textContent = text;
  }
}

if (!customElements.get('layers-add')) customElements.define('layers-add', LayersAdd);
